import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import axios from "../../axios_config";
import { BASE_API_URL } from "../../common/endpoints";
import InputControlButtons from "./input_control_buttons";
import { describeError } from "../utils/error_message";

// Components that render the inputs in a compact (40px) form factor.
const COMPACT_MODES = new Set(["activation_modal", "settings_modal", "request_mentor"]);
const isCompact = inComponent => COMPACT_MODES.has(inComponent);

// `settings`: the settings modal draws its fields flat, with the same corners as its other inputs.
const getLocationContainerClass = ({ withBorder, isError, disabled, isOpen, settings }) => {
	const border = isError
		? "border border-[red]"
		: withBorder
			? "border border-[#d6d6df] dark:border-white/10"
			: "";
	const radius = settings
		? isOpen
			? "rounded-t-md rounded-b-none"
			: "rounded-md"
		: isOpen
			? "rounded-t-[10px] rounded-b-none"
			: "rounded-[10px]";
	const opacity = disabled ? "opacity-50" : "";
	const shadow = disabled
		? ""
		: `${settings ? "" : "[box-shadow:0px_2px_8px_0px_rgba(0,0,0,0.1)]"} hover:[box-shadow:rgba(0,0,0,0.24)_0px_3px_8px] focus-within:[box-shadow:rgba(0,0,0,0.24)_0px_3px_8px]`;
	return `bg-white dark:bg-dark-gray-elevated overflow-hidden transition-[0.2s] ${border} ${radius} ${opacity} ${shadow}`
		.replace(/\s+/g, " ")
		.trim();
};

// The sign-in card: its own field look, the same search and selection logic.
const AUTH_MODE = "auth";

const getLandingContainerClass = ({ disabled, isOpen }) =>
	[
		"bg-auth-bg border border-auth-line overflow-hidden transition-colors focus-within:border-primary",
		isOpen ? "rounded-t-md rounded-b-none" : "rounded-md",
		disabled ? "opacity-50" : "",
	].join(" ");

const removeLastPart = text => {
	if (!text) return "";

	const parts = text.split(",");

	if (parts.length <= 1) {
		return "";
	}

	return parts.slice(0, -1).join(",").trim();
};

const UserLocationSelect = ({
	inComponent = "",
	userData = {},
	isCountryRequiredError = false,
	withBorder = false,
	handleUserData,
	// Passed by the profile form, which owns the save/cancel row; the mentor request has none.
	onClickCancelChanges = undefined,
	onClickSaveChanges = undefined,
}) => {
	const { t } = useTranslation();

	const [countryInput, setCountryInput] = useState("");
	const [cityInput, setCityInput] = useState("");
	const [countries, setCountries] = useState([]);
	const [cities, setCities] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState(null);
	const [selectedCity, setSelectedCity] = useState(null);
	const [shortCountryName, setShortCountryName] = useState(null);
	const [searchErrors, setSearchErrors] = useState({
		country: false,
		city: false,
	});
	const [debouncedCountryInput] = useDebounce(countryInput, 800);
	const [debouncedCityInput] = useDebounce(cityInput, 800);

	const countryAbortRef = useRef(null);
	const cityAbortRef = useRef(null);

	const compact = isCompact(inComponent);
	const isLanding = inComponent === AUTH_MODE;
	const containerClass = isLanding ? getLandingContainerClass : getLocationContainerClass;
	const isFilterMode = inComponent === "filter";
	const isSettings = inComponent === "settings_modal";
	const fieldHeightClass = isSettings
		? "h-[42px] max-[1024px]:h-[44px] text-[15px] max-[1024px]:text-[16px]"
		: compact
			? "h-[40px] text-[16px]"
			: "h-[52px] text-[16px]";
	const dropdownTopClass = isSettings
		? "top-[42px] max-[1024px]:top-[44px]"
		: compact
			? "top-[40px]"
			: "top-[52px]";

	const setUserCountryData = () => {
		setCountryInput(userData.user_country);
		setSelectedCountry(
			userData?.user_place_details?.country?.place_id
				? {
						place_id: userData.user_place_details.country.place_id,
						description: userData.user_country,
					}
				: null,
		);
	};

	const setUserCityData = () => {
		setCityInput(userData.user_city);
		setSelectedCity(
			userData?.user_place_details?.locality?.place_id
				? {
						place_id: userData.user_place_details.locality.place_id,
						description: userData.user_city,
					}
				: null,
		);
	};

	useEffect(() => {
		if (
			(inComponent === "settings_modal" ||
				inComponent === "filter" ||
				inComponent === "request_mentor" ||
				inComponent === AUTH_MODE) &&
			userData
		) {
			if (userData.user_country) {
				setCountryInput(userData.user_country);

				const countryPlaceId = userData?.user_place_details?.country?.place_id;
				const countryShort =
					userData?.user_place_details?.country?.address_components?.[0]?.short_name || null;

				setSelectedCountry(
					countryPlaceId ? { place_id: countryPlaceId, description: userData.user_country } : null,
				);

				setShortCountryName(countryShort);
			}

			if (userData.user_city) {
				setCityInput(userData.user_city);
				setSelectedCity(
					userData?.user_place_details?.locality?.place_id
						? {
								place_id: userData.user_place_details.locality.place_id,
								description: userData.user_city,
							}
						: null,
				);
			}
		}
	}, [inComponent, userData]);

	// The landing form remounts with a place already chosen; its first empty pass must not wipe the city.
	const skipFirstCityResetRef = useRef(isLanding);

	useEffect(() => {
		if (skipFirstCityResetRef.current) {
			skipFirstCityResetRef.current = false;
			return;
		}
		if (!selectedCountry && inComponent !== "settings_modal" && inComponent !== "filter") {
			setSelectedCity(null);
			setCityInput("");
			handleUserData("city", "");
			handleUserData("cityId", null);
		}
	}, [selectedCountry, inComponent]);

	const fetchCountries = async query => {
		const q = query.trim();

		// Cancel any pending request before starting a new one.
		countryAbortRef.current?.abort();

		if (q.length < 2) {
			setCountries([]);
			setSearchErrors(prev => ({ ...prev, country: false }));
			return;
		}

		const controller = new AbortController();
		countryAbortRef.current = controller;

		try {
			const response = await axios.get(`${BASE_API_URL}/v1/places`, {
				params: { input: q, types: "country" },
				signal: controller.signal,
			});

			if (response.data.status === "OK") {
				setCountries(response.data.places || []);
				setSearchErrors(prev => ({ ...prev, country: false }));
			} else {
				setCountries([]);
				setSearchErrors(prev => ({ ...prev, country: true }));
			}
		} catch (error) {
			if (
				error.code === "ERR_CANCELED" ||
				error.name === "CanceledError" ||
				error.name === "AbortError"
			) {
				return;
			}
			console.error("Error loading countries:", describeError(error));
			setCountries([]);
		}
	};

	const fetchCountryDetails = async countryId => {
		try {
			const response = await axios.get(`${BASE_API_URL}/v1/places/details`, {
				params: { placeId: countryId },
			});
			const shortName = response.data.address_components?.[0]?.short_name || null;

			setShortCountryName(shortName);
		} catch (error) {
			console.error("Error loading country details:", describeError(error));
			setShortCountryName(null);
		}
	};

	const fetchCities = async query => {
		const q = query.trim();

		cityAbortRef.current?.abort();

		if ((!isFilterMode && !shortCountryName) || q.length < 2) {
			setCities([]);
			setSearchErrors(prev => ({ ...prev, city: false }));
			return;
		}

		const controller = new AbortController();
		cityAbortRef.current = controller;

		try {
			const params = {
				input: q,
				types: "locality",
			};
			if (shortCountryName) {
				params.components = `country:${shortCountryName}`;
			}

			const response = await axios.get(`${BASE_API_URL}/v1/places`, {
				params,
				signal: controller.signal,
			});

			if (response.data.status === "OK") {
				setCities(response.data.places || []);
				setSearchErrors(prev => ({ ...prev, city: false }));
			} else {
				setCities([]);
				setSearchErrors(prev => ({ ...prev, city: true }));
			}
		} catch (error) {
			if (
				error.code === "ERR_CANCELED" ||
				error.name === "CanceledError" ||
				error.name === "AbortError"
			) {
				return;
			}
			console.error("Error loading cities:", describeError(error));
			setCities([]);
		}
	};

	useEffect(() => {
		if (selectedCountry && !shortCountryName && !isFilterMode) {
			fetchCountryDetails(selectedCountry.place_id);
		}
	}, [selectedCountry, shortCountryName, isFilterMode]);

	useEffect(() => {
		if (selectedCountry || countryInput.trim() === "") {
			setCountries([]);
			setSearchErrors(prevState => ({ ...prevState, country: false }));
			return;
		}

		fetchCountries(debouncedCountryInput);
	}, [debouncedCountryInput]);

	useEffect(() => {
		if (
			!selectedCity &&
			selectedCountry &&
			(isFilterMode || shortCountryName) &&
			debouncedCityInput.length >= 2
		) {
			fetchCities(debouncedCityInput);
		} else {
			setCities([]);
			setSearchErrors(prevState => ({ ...prevState, city: false }));
			return;
		}
	}, [debouncedCityInput, selectedCountry, selectedCity, shortCountryName, isFilterMode]);

	useEffect(() => {
		if (selectedCountry) {
			handleUserData("country", selectedCountry.description);
			handleUserData("countryId", selectedCountry.place_id);
		} else {
			handleUserData("country", "");
			handleUserData("countryId", null);
		}
	}, [selectedCountry]);

	useEffect(() => {
		if (selectedCity) {
			const cityName =
				selectedCity.structured_formatting?.main_text || selectedCity.description || "";

			handleUserData("city", cityName);
			handleUserData("cityId", selectedCity.place_id);
		} else {
			handleUserData("city", "");
			handleUserData("cityId", null);
		}
	}, [selectedCity]);

	const resetCityState = () => {
		setSelectedCity(null);
		setCityInput("");
		cityAbortRef.current?.abort();
		setCities([]);
	};

	const handleCountryChange = useCallback(
		value => {
			setCountryInput(value);

			if (value.trim() === "") {
				setSelectedCountry(null);
				setCountries([]);
				resetCityState();
				return;
			}

			if (selectedCountry && value.trim() !== selectedCountry.description) {
				setSelectedCountry(null);
				setShortCountryName(null);
				resetCityState();
			}
		},
		[selectedCountry],
	);

	const handleCityChange = value => {
		setCityInput(value);
		setSelectedCity(null);
	};

	const selectCountry = (event, country) => {
		event.stopPropagation();
		setSelectedCountry(country);
		setCountryInput(country.description);
		setCountries([]);
		if (country.place_id !== userData?.user_place_details?.country?.place_id) {
			setSelectedCity(null);
			resetCityState();
		}
	};

	const selectCity = (event, city) => {
		event.stopPropagation();
		setCityInput(city.structured_formatting?.main_text || city.description || "");
		setSelectedCity(city);
		setCities([]);
	};

	//                         Logic for control buttons (save/cancel)

	const onClickCountryCancelButton = event => {
		onClickCancelChanges(event, "country");
		setUserCountryData();
		setUserCityData();
	};

	const onClickCountrySaveButton = event => {
		onClickSaveChanges(event, "country");
	};

	const onClickCityCancelButton = event => {
		onClickCancelChanges(event, "city");
		setUserCityData();
	};

	const onClickCitySaveButton = event => {
		onClickSaveChanges(event, "city");
	};

	const showCountryControlButtons = useMemo(() => {
		if (inComponent !== "settings_modal" || !userData._id) return false;

		const hasValidUserCountry = userData.user_country || userData.user_country === "";

		return Boolean(hasValidUserCountry && userData.user_country !== countryInput);
	}, [countryInput, inComponent, userData]);

	const showCityControlButtons = useMemo(() => {
		if (inComponent !== "settings_modal" || !userData._id) return false;

		const countryIsChanged =
			!selectedCountry ||
			selectedCountry.place_id !== userData?.user_place_details?.country?.place_id;

		if (countryIsChanged) return false;

		const hasValidUserCity = userData.user_city || userData.user_city === "";

		return Boolean(
			hasValidUserCity &&
			userData.user_city !== cityInput &&
			(cityInput === "" || selectedCity?.place_id),
		);
	}, [cityInput, inComponent, userData, selectedCity, selectedCountry]);

	const dropdownClass = isLanding
		? "w-full list-none m-0 p-1 max-h-[168px] overflow-y-auto bg-auth-card border-t border-auth-line"
		: `w-full list-none p-[8px_4px] overflow-y-auto bg-white dark:bg-dark-gray-elevated border-t border-border dark:border-white/10 transition-[0.2s] [&::-webkit-scrollbar]:w-[3px] ${inComponent !== "request_mentor" ? "max-h-[168px]" : "max-h-[125px]"}`;
	const dropdownItemClass = isLanding
		? "px-2 py-2 text-base rounded-xs text-auth-text cursor-pointer hover:bg-primary/10"
		: "p-[8px_6px] text-[16px] rounded-[6px] text-text-primary dark:text-text-dark-gray cursor-pointer hover:bg-primary/20";
	const dropdownContainerClass = isLanding
		? "absolute left-0 top-12 w-full overflow-hidden h-max rounded-b-md bg-auth-card border border-t-0 border-auth-line shadow-md z-[999] empty:hidden"
		: `absolute left-0 w-full overflow-hidden h-max ${isSettings ? "rounded-b-md" : "rounded-b-[10px]"} bg-white dark:bg-dark-gray-elevated [box-shadow:rgba(50,50,93,0.25)_0px_6px_12px_-2px,rgba(0,0,0,0.3)_0px_3px_7px_-3px] z-[999] ${dropdownTopClass}`;
	const inputClass = isLanding
		? "w-full h-12 pl-4 text-base text-auth-text placeholder:text-auth-sub bg-transparent outline-none"
		: `w-full pl-[15px] text-text-primary dark:text-text-dark-gray placeholder:text-text-secondary dark:placeholder:text-text-dark-gray-muted font-normal leading-6 transition-[0.2s] bg-transparent ${fieldHeightClass}`;
	const notFoundClass = isLanding
		? "border-t border-auth-line bg-auth-card px-4 py-2.5 select-none text-auth-sub text-base"
		: "border-t border-border dark:border-white/10 bg-white dark:bg-dark-gray-elevated p-[10px_15px] select-none text-text-primary dark:text-text-dark-gray";
	// Reserve room on the right for the control buttons so the input text never hides behind them.
	const inputPaddingClass = showButtons => (showButtons ? "pr-[66px]" : "pr-[15px]");

	return (
		<div
			className={`w-full flex [&>*]:flex-1 [&>*]:min-w-0 ${isLanding ? "gap-3 max-[420px]:flex-col" : isSettings ? "gap-3 max-[1024px]:flex-col" : "gap-[10px] max-[700px]:flex-col"}`}
		>
			<div className="relative block w-full">
				<div className="relative">
					<div
						className={containerClass({
							withBorder,
							isError: inComponent === "request_mentor" && isCountryRequiredError,
							isOpen: countries.length > 0 || searchErrors.country,
							settings: isSettings,
						})}
					>
						<div className="relative" data-testid="country-input-container">
							<input
								className={`${inputClass} ${inputPaddingClass(showCountryControlButtons)}`}
								placeholder={t("common-components.user-location-select.country-placeholder")}
								value={countryInput}
								onChange={e => handleCountryChange(e.target.value)}
								autoComplete="off"
								data-testid="country-input"
							/>
							{showCountryControlButtons && (
								<InputControlButtons
									onClickCancelButton={onClickCountryCancelButton}
									onClickSaveButton={onClickCountrySaveButton}
									showSaveButton={countryInput === "" || !!selectedCountry?.place_id}
									data-testid="control-buttons"
								/>
							)}
						</div>

						<div className={dropdownContainerClass} data-testid="country-dropdown-container">
							{countries.length > 0 && (
								<ul className={dropdownClass}>
									{countries.map(country => (
										<li
											key={country.place_id}
											className={dropdownItemClass}
											onClick={e => selectCountry(e, country)}
											data-testid="country-dropdown-item"
										>
											{country.description}
										</li>
									))}
								</ul>
							)}

							{searchErrors.country && (
								<div className={notFoundClass} data-testid="country-not-found-text">
									{t("common-components.user-location-select.country-not-found")}
								</div>
							)}
						</div>

						{inComponent === "activation_modal" && (
							<div className="absolute top-[-8px] right-[-1px] text-primary font-semibold select-none">
								*
							</div>
						)}
					</div>

					{inComponent === "request_mentor" && isCountryRequiredError && (
						<div className="absolute bottom-[-14px] left-0 text-[12px] text-[red]">
							{t("ask-question-modal.required-field-error")}
						</div>
					)}
				</div>
			</div>

			{inComponent !== "request_mentor" && (
				<div className="relative block w-full">
					<div
						className={containerClass({
							withBorder,
							disabled:
								!selectedCountry ||
								(!isFilterMode && !shortCountryName) ||
								showCountryControlButtons,
							isOpen: cities.length > 0 || searchErrors.city,
							settings: isSettings,
						})}
					>
						<div className="relative" data-testid="city-input-container">
							<input
								className={`${inputClass} ${inputPaddingClass(showCityControlButtons)}`}
								placeholder={t("common-components.user-location-select.city-placeholder")}
								value={cityInput}
								onChange={e => handleCityChange(e.target.value)}
								autoComplete="off"
								disabled={
									!selectedCountry ||
									(!isFilterMode && !shortCountryName) ||
									showCountryControlButtons
								}
								data-testid="city-input"
							/>
							{showCityControlButtons && (
								<InputControlButtons
									onClickCancelButton={onClickCityCancelButton}
									onClickSaveButton={onClickCitySaveButton}
									showSaveButton={cityInput === "" || !!selectedCity?.place_id}
								/>
							)}
						</div>

						<div className={dropdownContainerClass} data-testid="cities-dropdown-container">
							{cities.length > 0 && (
								<ul className={dropdownClass}>
									{cities.map(city => (
										<li
											key={city.place_id}
											className={dropdownItemClass}
											onClick={e => selectCity(e, city)}
											data-testid="city-dropdown-item"
										>
											{city.structured_formatting?.main_text}{" "}
											<span className="inline-block mt-[5px] text-[#acacac] dark:text-text-dark-gray-muted text-[14px]">
												{removeLastPart(city.structured_formatting?.secondary_text)}
											</span>
										</li>
									))}
								</ul>
							)}

							{searchErrors.city && (
								<div className={notFoundClass} data-testid="city-not-found-text">
									{t("common-components.user-location-select.city-not-found")}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default memo(UserLocationSelect);
